import { z } from 'zod';

const UserPreferences = z.object({
  Paperless: z.string().or(z.boolean()).optional(),
  PromotionalStatus: z.string().or(z.boolean()).optional(),
  PromotionalCommType: z.string().optional(),
  ServiceAlertsCommType: z.string().optional(),
  ServiceAlertsStatus: z.string().or(z.boolean()).optional(),
  PhoneNumber: z.string(),
  MobileNumber: z.string().optional(),
  OBPStatus: z.string().or(z.boolean()),
  IsSettingUpdated: z.string().or(z.boolean()).optional(),
});

const UserMetadata = z.object({
  first_name: z.string().optional(),
  givenName: z.string().optional(),
  given_name: z.string().optional(),
  last_name: z.string().optional(),
  name: z.string().optional(),
  user_preferences: z.optional(UserPreferences),
  default_account: z.string().optional(),
  defaultPaymentMethodId: z.string().or(z.number()).optional(),
  default_portal: z.string().optional(),
  address: z.string().optional(),
  roles: z.array(z.string()).optional(),
  expiryNotification: z.any().optional(),
  disposalSiteStates: z.array(z.string()).optional(),
  title: z.string().optional(),
  company: z.string().optional(),
  requirePasswordReset: z.boolean().optional(),
});

const AppMetadata = z.object({
  fis_username: z.string(),
  user_role: z.string().optional(),
  cdh_contact_profile_id: z.string(),
  registration_status: z.enum(['CD', 'AA']).optional(),
  user_type: z.enum(['OR', 'NR', 'CR']),
  fis_sync_status: z.enum(['CD', 'NA']).optional(),
  sso_flag: z.enum(['Y', 'N']).optional(),
  cdh_id: z.string(),
  mr_original_id: z.string().optional(),
  _comment_as4: z.string().optional(),
  _comment_as1: z.string().optional(),
  additional_portals: z.array(z.string()).optional(),
  approval_statuses: z
    .array(
      z.object({
        application_name: z.string(),
        source_name: z.string(),
        status: z.string(),
        user_active: z.boolean(),
      })
    )
    .optional(),
  onboardingComplete: z.boolean().optional(),
  importGenerators: z.boolean().optional(),
  importProfiles: z.boolean().optional(),
  permissions: z.array(z.string()).optional(),
  customerName: z.string().optional(),
  access: z.array(z.string()).optional(),
  cor_default_site: z.any().optional(),
  account_details: z.array(z.any()).optional(),
  cor_user_id: z.string().optional(),
});

const ProfileIdentity = z.object({
  user_id: z.string(),
  provider: z.string(),
  connection: z.string(),
  isSocial: z.boolean(),
});

export const UserProfile = z.object({
  user_metadata: UserMetadata,
  app_metadata: AppMetadata,
  email: z.string().optional(),
  name: z.string().optional(),
  picture: z.string().optional(),
  nickname: z.string().optional(),
  fis_username: z.string().optional(),
  user_role: z.string().optional(),
  cdh_contact_profile_id: z.string().optional(),
  registration_status: z.literal('CD').optional(),
  user_type: z.literal('OR').optional(),
  fis_sync_status: z.literal('CD').optional(),
  sso_flag: z.enum(['Y', 'N']).optional(),
  cdh_id: z.string().optional(),
  mr_original_id: z.string().optional(),
  email_verified: z.boolean(),
  clientID: z.string().optional(),
  updated_at: z.string().optional(),
  user_id: z.string().optional(),
  identities: z.array(ProfileIdentity).optional(),
  created_at: z.string().optional(),
  sub: z.string().optional(),
  entry_url: z.string().optional(),
  last_password_reset: z.string().optional(),
  impersonator: z
    .object({
      user_id: z.string(),
      email: z.string(),
    })
    .optional(),
  impersonated: z.boolean().optional(),
  _comment_as4: z.string().optional(),
  _comment_as1: z.string().optional(),
});

export type UserProfile = z.infer<typeof UserProfile>;
export type UserMetadata = UserProfile['user_metadata'];
